import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, lastValueFrom } from 'rxjs';
import { AuthApiService } from '../../../services/auth.api.service';
import { UserState } from '../../../states/user-state';
import { ReLoginWithTokenPayload } from '../../../../shared/models/auth/re-login-with-token-payload';
import { StepOneState } from '../../../states/loan-steps/step-one-state';
import { StepTwoState } from '../../../states/loan-steps/step-two-state';
import { StepThreeState } from '../../../states/loan-steps/step-three-state';
import { StepFourState } from '../../../states/loan-steps/step-four-state';
import { LoanApplicationService } from '../../../services/loan/loan-application.api.service';
import { ApplicantService } from '../../../services/loan/applicant.api.service';
import { JointApplicantService } from '../../../services/loan/joint-applicant.api.service';
import { NonjointApplicantService } from '../../../services/loan/nonjoint-applicant.api.service';
import { PDFApplicationService } from '../../../services/loan/pdf-application.api.service';
import { PrintPdfService } from '../../../services/pdf/print-pdf.service';
import { StepOneModel } from '../../../../shared/models/states/loan-step-one.model';
import { ApplicantModel } from '../../../../shared/models/states/applicant.model';

type OpenType = 'Applicant' | 'Nonjoint';
type OpenNoncePayload = {
  token: string;
  email: string;
  type: OpenType;
  loanId: string;
  applicantId: string;
  reference?: string;
};

@Component({
  selector: 'app-open-application',
  templateUrl: './open-application.component.html',
  styleUrl: './open-application.component.scss',
  standalone: false,
})
export class OpenApplicationComponent implements OnInit {
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthApiService,
    private userState: UserState,
    private s1: StepOneState,
    private s2: StepTwoState,
    private s3: StepThreeState,
    private s4: StepFourState,
    private loanApplicationService: LoanApplicationService,
    private applicantService: ApplicantService,
    private jointApplicantService: JointApplicantService,
    private nonjointApplicantService: NonjointApplicantService,
    private pdfService: PDFApplicationService,
    private printPdf: PrintPdfService,
  ) {}

  async ngOnInit(): Promise<void> {
    const qp = this.route.snapshot.queryParamMap;
    const nonce = qp.get('nonce') ?? undefined;
    let payload: OpenNoncePayload | undefined;
    if (nonce) payload = this.consumeNoncePayload(nonce);

    const type = (payload?.type ?? (qp.get('type') as OpenType | null) ?? 'Applicant') as OpenType;
    const loanId = payload?.loanId ?? qp.get('loanId') ?? undefined;
    const applicantId = payload?.applicantId ?? qp.get('applicantId') ?? undefined;
    const reference = payload?.reference ?? qp.get('reference') ?? undefined;
    const wantPrint = qp.get('print') === '1' || qp.get('print') === 'true';

    if (!loanId || !applicantId) {
      await this.router.navigate(['/loan/application-list']);
      return;
    }

    try {
      if (payload && !this.userState.getAuthenticated()) {
        await this.oneTimeTokenLogin(payload.email, payload.token);
      }

      if (wantPrint) {
        const refForPdf = reference ?? loanId;
        const { html } = await lastValueFrom(
          this.pdfService.getLoanApplicationHtml(loanId, applicantId, refForPdf),
        );
        await this.printPdf.printHtml(html, {
          title: `loan_application_${refForPdf}`,
        });
        await this.router.navigate(['/loan/application-list']);
        return;
      }

      this.s1.referenceNumber = reference;

      if (type === 'Nonjoint') {
        const { loan, applicant } = await lastValueFrom(
          forkJoin({
            loan: this.loanApplicationService.getLoanApplicationById(loanId),
            applicant: this.applicantService.getApplicantById(applicantId),
          }),
        );

        const loanState = loan as StepOneModel;
        const applicantState = applicant as ApplicantModel;
        applicantState.applicant_type = 'Nonjoint';

        this.s1.state = loanState;
        this.s2.state = {
          loan_id: loanState.uuid!,
          applicant: applicantState,
          isIndividualApplicant: false,
        };
        this.s3.state = undefined;

        await this.s4.fromDB();
        await this.router.navigate(['/loan/step-two']);
        return;
      }

      const { loan, applicant, joints, nonjoints } = await lastValueFrom(
        forkJoin({
          loan: this.loanApplicationService.getLoanApplicationById(loanId),
          applicant: this.applicantService.getApplicantById(applicantId),
          joints: this.jointApplicantService.getJointApplicantsByLoanId(loanId),
          nonjoints: this.nonjointApplicantService.getNonjointApplicantsByLoanId(loanId),
        }),
      );

      const loanState = loan as StepOneModel;
      const applicantState = applicant as ApplicantModel;
      const jointApplicants = joints as ApplicantModel[];
      const nonjointApplicants = nonjoints as ApplicantModel[];
      const allApplicants = jointApplicants.concat(nonjointApplicants);

      this.s1.state = loanState;
      this.s2.state = {
        loan_id: loanState.uuid!,
        applicant: applicantState,
        isIndividualApplicant: allApplicants.length === 0,
      };
      this.s3.state = { applicants: allApplicants };

      await this.s4.fromDB();
      await this.router.navigate(['/loan/step-one']);
    } catch (err) {
      console.error('Unable to open application', err);
      await this.router.navigate(['/login']);
    }
  }

  private consumeNoncePayload(nonce: string): OpenNoncePayload | undefined {
    const key = `__cb_open_app__:${nonce}`;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return undefined;
      localStorage.removeItem(key);
      const parsed = JSON.parse(raw) as OpenNoncePayload;
      if (!parsed?.token || !parsed?.email) return undefined;
      return parsed;
    } catch {
      try {
        localStorage.removeItem(key);
      } catch {
        // ignore
      }
      return undefined;
    }
  }

  private async oneTimeTokenLogin(email: string, token: string): Promise<void> {
    const payload = new ReLoginWithTokenPayload(email, false, token);
    const user = await lastValueFrom(this.authService.authenticateWithToken(payload));
    this.userState.setUser(user, false);
  }
}

